import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import React, { useEffect } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { v4 } from "uuid";

import { FieldErrors } from "src/api/types";
import { Button } from "src/base-components/Button";
import { ClauseRow } from "src/clauseBlock/ClauseRow";
import { Clause, Operators, Junctions } from "src/clients/flow-api";

type Props = {
  clauses: Clause[];
  junction: Junctions;
  immutable: boolean;
  prefixClassName?: string;
  junctionWidth?: `w-${number}`;
  fieldPlaceholder?: string;
  valuePlaceholder?: string;
  allowDeletingAllClauses?: boolean;
  onChange?: (value: ClauseForm) => void;
  fieldErrors?: FieldErrors;
  hideActionButtons?: boolean;
  lazyInputs?: boolean;
};

export type ClauseForm = {
  junction: Junctions;
  clauses: Clause[];
};

export const ClauseBlock: React.FC<Props> = ({
  clauses,
  junction,
  immutable,
  prefixClassName = "w-28",
  junctionWidth = "w-24",
  fieldPlaceholder = "data.field",
  valuePlaceholder = '"comparison value"',
  allowDeletingAllClauses,
  onChange,
  fieldErrors,
  hideActionButtons,
  lazyInputs = false,
}) => {
  const { control, setValue, watch } = useForm<ClauseForm>({
    defaultValues: { junction, clauses },
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "clauses",
  });

  useEffect(() => {
    const { unsubscribe } = watch(({ clauses, junction }) => {
      onChange?.({ clauses, junction } as ClauseForm);
    });

    return () => unsubscribe();
  }, [onChange, watch]);

  return (
    <>
      {fields.map((clause, index) => (
        <ClauseRow
          key={clause.id_left}
          allowDeletingAllClauses={allowDeletingAllClauses}
          control={control}
          errorLeft={fieldErrors?.[clause.id_left]}
          errorRight={fieldErrors?.[clause.id_right]}
          fieldPlaceholder={fieldPlaceholder}
          immutable={immutable}
          index={index}
          junctionWidth={junctionWidth}
          lazyInputs={lazyInputs}
          length={fields.length}
          prefixClassName={prefixClassName}
          setValue={setValue}
          valuePlaceholder={valuePlaceholder}
          onDeleteClause={() => remove(index)}
        />
      ))}
      {!hideActionButtons && (
        <span className="mt-2 inline-block">
          <Button
            dataLoc="add-condition"
            disabled={immutable}
            iconLeft={faPlus}
            size="sm"
            variant="secondary"
            onClick={() =>
              append({
                id_left: v4(),
                id_right: v4(),
                left: "",
                right: "",
                operator: Operators.EQ,
              })
            }
          >
            Add condition
          </Button>
        </span>
      )}
    </>
  );
};
